import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Package, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Truck, CheckCircle2, Clock3,
  ShoppingBag, ImagePlus, X, BarChart3, Settings2, RefreshCw, Search, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { API } from "@/lib/api";
import { refreshShopStatus } from "@/lib/shop";
import { RichTextEditor } from "@/components/RichTextEditor";

export const lightCard = "rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]";
const inputCls = "rounded-xl border-brand/20 bg-white h-10 focus-visible:ring-brand";

const ORDER_STATUS_STYLE = {
  new: "bg-sky-100 text-sky-800",
  preparing: "bg-blue-100 text-blue-800",
  awaiting_supplier: "bg-amber-100 text-amber-800",
  ready: "bg-bamboo-soft text-bamboo-deep",
  completed: "bg-brand-soft text-brand",
  cancelled: "bg-rose-100 text-rose-700",
};

export const OrderBadge = ({ status, labels }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${ORDER_STATUS_STYLE[status] || "bg-stone-alt text-brand-ink"}`} data-testid={`shop-status-${status}`}>
    {labels[status] || status}
  </span>
);

const fmtD = (iso, lang) => {
  try {
    return new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
};

const SUPPLY_STYLE = {
  to_order: "bg-rose-100 text-rose-700",
  ordered: "bg-amber-100 text-amber-800",
  received: "bg-bamboo-soft text-bamboo-deep",
};

// ---------------------------------------------------------------------------
// Orders (both roles)
// ---------------------------------------------------------------------------
export const ShopOrdersPanel = () => {
  const { t, lang } = useLang();
  const s = t.ashop;
  const [orders, setOrders] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [extDays, setExtDays] = useState({});

  const load = useCallback(() => {
    api.get("/admin/shop/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  useEffect(load, [load]);

  const setStatus = async (o, status) => {
    try {
      await api.patch(`/admin/shop/orders/${o.id}/status`, { status });
      toast.success(s.status_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const extend = async (o) => {
    try {
      await api.post(`/admin/shop/orders/${o.id}/extend`, { days: Number(extDays[o.id] || 3) });
      toast.success(s.extended_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (orders === null) return null;
  if (orders.length === 0) return <div className={`${lightCard} text-center text-brand-ink/50`} data-testid="shop-orders-empty">{s.empty_orders}</div>;

  return (
    <div className="space-y-4" data-testid="shop-orders-panel">
      {orders.map((o) => {
        const finalized = o.status === "completed" || o.status === "cancelled";
        return (
          <div key={o.id} className={lightCard} data-testid={`shop-order-${o.order_number}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-brand">{o.order_number}</span>
                  <OrderBadge status={o.status} labels={s.statuses} />
                  {o.coupon_code && (
                    <span className="rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5">
                      {s.coupon_badge} {o.coupon_discount} · {o.coupon_code}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-sm font-bold text-brand-ink">{o.customer_name}</p>
                <p className="text-xs text-brand-ink/60">
                  <a href={`tel:${o.phone}`} className="font-semibold text-brand">{o.phone}</a> · {o.email} · {fmtD(o.created_at, lang)}
                </p>
                {o.notes && <p className="mt-1 text-xs italic text-brand-ink/50">"{o.notes}"</p>}
                {o.status === "ready" && o.ready_deadline && (
                  <p className="mt-1 text-xs font-bold text-bamboo-deep flex items-center gap-1">
                    <Clock3 className="h-3.5 w-3.5" /> {s.ready_deadline}: {o.ready_deadline}
                    {o.extensions > 0 && <span className="text-brand-ink/40">(+{o.extensions} επ.)</span>}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-heading text-2xl font-bold text-brand-ink">€{o.total.toFixed(2)}</p>
                {!finalized && (
                  <div className="mt-2">
                    <Select value={o.status} onValueChange={(v) => setStatus(o, v)}>
                      <SelectTrigger className="w-[190px] rounded-full border-brand/20 bg-stone-alt/50 h-9 text-sm" data-testid={`shop-order-status-${o.order_number}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["new", "preparing", "awaiting_supplier", "ready", "completed", "cancelled"].map((st) => (
                          <SelectItem key={st} value={st}>{s.statuses[st]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 rounded-xl bg-stone-alt/40 p-3 space-y-1">
              {o.items.map((i, k) => (
                <div key={k} className="flex items-center justify-between gap-2 text-sm text-brand-ink/80">
                  <span className="min-w-0 truncate">
                    {i.name_gr}{i.variant_label ? ` (${i.variant_label})` : ""} × {i.qty}
                    {i.supply && (
                      <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${SUPPLY_STYLE[i.supply]}`} data-testid={`supply-${o.order_number}-${k}`}>
                        {s.statuses[`supply_${i.supply}`]}
                      </span>
                    )}
                  </span>
                  <span className="font-semibold whitespace-nowrap">€{i.line_total.toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={() => setOpenId(openId === o.id ? null : o.id)} className="inline-flex items-center gap-1 text-xs font-bold text-brand hover:text-brand-hover no-a11y-underline" data-testid={`shop-history-toggle-${o.order_number}`}>
                {openId === o.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {s.history}
              </button>
              {o.status === "ready" && (
                <span className="inline-flex items-center gap-1.5 ml-auto">
                  <Input
                    type="number" min={1} max={15}
                    value={extDays[o.id] ?? 3}
                    onChange={(e) => setExtDays({ ...extDays, [o.id]: e.target.value })}
                    className="w-16 h-8 rounded-lg border-brand/20 text-xs"
                    data-testid={`extend-days-${o.order_number}`}
                  />
                  <span className="text-xs text-brand-ink/50">{s.extend_days}</span>
                  <Button onClick={() => extend(o)} size="sm" variant="outline" className="rounded-full border-brand/30 text-brand font-bold h-8 text-xs" data-testid={`extend-btn-${o.order_number}`}>
                    {s.extend_btn}
                  </Button>
                </span>
              )}
            </div>

            {openId === o.id && (
              <div className="mt-3 rounded-xl bg-brand-soft/30 border border-brand/10 p-4 space-y-2" data-testid={`shop-history-${o.order_number}`}>
                {(o.status_history || []).map((h, k) => (
                  <div key={k} className="flex items-center gap-2 text-xs text-brand-ink/70 flex-wrap">
                    <span className="font-mono">{fmtD(h.at, lang)}</span>
                    <span className="font-bold">{s.statuses[h.status] || h.status}</span>
                    <span className="text-brand-ink/40">· {h.by}</span>
                    {h.note && <span className="italic text-brand-ink/50">— {h.note}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Supplier orders (both roles) — items pending from suppliers, grouped
// ---------------------------------------------------------------------------
export const SupplierOrdersPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [groups, setGroups] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/shop/supplier-orders").then((r) => setGroups(r.data)).catch(() => setGroups([]));
  }, []);
  useEffect(load, [load]);

  const mark = async (item, stage) => {
    try {
      await api.post(`/admin/shop/orders/${item.order_id}/items/${item.item_index}/supply`, { stage });
      toast.success(s.status_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (groups === null) return null;
  return (
    <div className="space-y-5" data-testid="supplier-orders-panel">
      <p className="text-sm text-brand-ink/60">{s.supplier_orders_hint}</p>
      {groups.length === 0 ? (
        <div className={`${lightCard} text-center text-brand-ink/50`} data-testid="supplier-orders-empty">
          <CheckCircle2 className="h-8 w-8 mx-auto text-bamboo-deep mb-2" /> {s.no_supplier_items}
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.supplier_id} className={lightCard} data-testid={`supplier-group-${g.supplier_name}`}>
            <p className="font-heading text-xl font-bold text-brand-ink flex items-center gap-2">
              <Truck className="h-5 w-5 text-brand" /> {g.supplier_name}
              <span className="ml-auto rounded-full bg-amber-100 text-amber-800 text-xs font-bold px-3 py-0.5">{g.items.length}</span>
            </p>
            <div className="mt-3 space-y-2">
              {g.items.map((i) => (
                <div key={`${i.order_id}-${i.item_index}`} className="flex flex-wrap items-center gap-3 rounded-xl bg-stone-alt/40 px-3.5 py-2.5" data-testid={`supplier-item-${i.order_number}-${i.item_index}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-brand-ink truncate">
                      {i.name_gr}{i.variant_label ? ` (${i.variant_label})` : ""} <span className="text-brand">{s.qty_short}{i.qty}</span>
                    </p>
                    <p className="text-xs text-brand-ink/50">{s.for_order} <span className="font-mono font-bold">{i.order_number}</span> · {i.customer_name}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${SUPPLY_STYLE[i.stage]}`}>{s.statuses[`supply_${i.stage}`]}</span>
                  {i.stage === "to_order" && (
                    <Button onClick={() => mark(i, "ordered")} size="sm" variant="outline" className="rounded-full border-amber-400 text-amber-700 font-bold h-8 text-xs" data-testid={`mark-ordered-${i.order_number}-${i.item_index}`}>
                      {s.mark_ordered}
                    </Button>
                  )}
                  {i.stage === "ordered" && (
                    <Button onClick={() => mark(i, "received")} size="sm" className="rounded-full bg-bamboo hover:bg-bamboo-hover text-white font-bold h-8 text-xs" data-testid={`mark-received-${i.order_number}-${i.item_index}`}>
                      {s.mark_received}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Products — dev: full CRUD + images; groomer: availability only
// ---------------------------------------------------------------------------
const emptyProduct = () => ({
  name_gr: "", name_en: "", description_gr: "", description_en: "",
  price: "", brand: "", category_id: "", supplier_id: "", active: true, online_orderable: true,
  sizes: [], colors: [], custom_axis_name_gr: "", custom_axis_name_en: "", custom_axis_options: [],
  variants: [{ label: "", size: "", color: "", custom: "", stock: 0, availability: "in_stock", lead_days: "", price: "", sale_price: "" }],
});

// Chip list editor — type + Enter to add
const ChipInput = ({ values, onChange, placeholder, hint, testid }) => {
  const [txt, setTxt] = useState("");
  const add = () => {
    const v = txt.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setTxt("");
  };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-full bg-brand-soft/70 border border-brand/20 text-brand-ink text-xs font-bold px-2.5 py-1" data-testid={`${testid}-chip-${v}`}>
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} className="text-brand/60 hover:text-rose-500" data-testid={`${testid}-chip-x-${v}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          value={txt}
          onChange={(e) => setTxt(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          onBlur={add}
          placeholder={placeholder}
          className="flex-1 min-w-28 h-8 rounded-lg border border-brand/20 bg-white px-2 text-xs focus:outline-none focus:ring-1 focus:ring-brand"
          data-testid={`${testid}-input`}
        />
      </div>
      {hint && <p className="mt-1 text-[11px] text-brand-ink/45">{hint}</p>}
    </div>
  );
};

export const ShopProductsPanel = ({ devMode }) => {
  const { t } = useLang();
  const s = t.ashop;
  const shop = t.shop;
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [colorsLib, setColorsLib] = useState([]);
  const [editing, setEditing] = useState(null); // {form, id|null}
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const dragProd = useRef(null);

  const onProdDrop = async (targetId) => {
    const from = dragProd.current;
    dragProd.current = null;
    if (!from || from === targetId || query.trim()) return;
    const ids = products.map((p) => p.id);
    const next = ids.filter((x) => x !== from);
    next.splice(next.indexOf(targetId), 0, from);
    setProducts(next.map((id) => products.find((p) => p.id === id)));
    try {
      await api.put("/admin/shop/products/reorder", { ids: next });
    } catch {
      toast.error("Error");
      load();
    }
  };

  const load = useCallback(() => {
    api.get("/admin/shop/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
    api.get("/shop/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/admin/shop/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/shop/colors").then((r) => setColorsLib(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    const f = editing.form;
    setSaving(true);
    const payload = {
      ...f,
      price: parseFloat(f.price) || 0,
      category_id: f.category_id || null,
      supplier_id: f.supplier_id || null,
      sizes: f.sizes || [],
      colors: f.colors || [],
      custom_axis_name_gr: f.custom_axis_name_gr || "",
      custom_axis_name_en: f.custom_axis_name_en || "",
      custom_axis_options: f.custom_axis_options || [],
      variants: f.variants.map((v) => ({ id: v.id || undefined, label: v.label || "", size: v.size || "", color: v.color || "", custom: v.custom || "", stock: parseInt(v.stock) || 0, availability: v.availability, lead_days: v.lead_days === "" || v.lead_days == null ? null : parseInt(v.lead_days), price: v.price === "" || v.price == null ? null : parseFloat(v.price), sale_price: v.sale_price === "" || v.sale_price == null ? null : parseFloat(v.sale_price) })),
    };
    try {
      if (editing.id) await api.put(`/admin/shop/products/${editing.id}`, payload);
      else await api.post("/admin/shop/products", payload);
      toast.success(s.saved_toast);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (!window.confirm(s.delete_confirm)) return;
    await api.delete(`/admin/shop/products/${p.id}`);
    toast.success(s.deleted_toast);
    load();
  };

  const uploadImage = async (p, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/shop/products/${p.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(s.saved_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteImage = async (p, idx) => {
    await api.delete(`/admin/shop/products/${p.id}/images/${idx}`);
    load();
  };

  const assignImage = async (p, idx, variantId) => {
    try {
      await api.put(`/admin/shop/products/${p.id}/images/${idx}/variant`, { variant_id: variantId || null });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const setAvail = async (p, variantId, patch) => {
    const v = p.variants.find((x) => x.id === variantId);
    try {
      await api.patch(`/admin/shop/products/${p.id}/availability`, {
        variant_id: variantId,
        availability: patch.availability ?? v.availability,
        stock: patch.stock !== undefined ? parseInt(patch.stock) || 0 : v.stock,
        lead_days: patch.lead_days !== undefined ? parseInt(patch.lead_days) || null : v.lead_days,
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const setPrice = async (p, variantId, patch) => {
    const v = p.variants.find((x) => x.id === variantId);
    const num = (val) => (val === "" || val == null ? null : parseFloat(val));
    try {
      await api.patch(`/admin/shop/products/${p.id}/variant-price`, {
        variant_id: variantId,
        price: patch.price !== undefined ? num(patch.price) : v.price,
        sale_price: patch.sale_price !== undefined ? num(patch.sale_price) : v.sale_price,
      });
      toast.success(s.saved_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (products === null) return null;
  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name_gr]));
  const supMap = Object.fromEntries(suppliers.map((x) => [x.id, x.name]));
  const q = query.trim().toLowerCase();
  const filtered = q
    ? products.filter((p) => [p.name_gr, p.name_en, p.brand, catMap[p.category_id], supMap[p.supplier_id]].filter(Boolean).some((x) => String(x).toLowerCase().includes(q)))
    : products;

  return (
    <div className="space-y-4" data-testid="shop-products-panel">
      {!devMode && <p className="text-sm text-brand-ink/60 bg-brand-soft/40 border border-brand/10 rounded-2xl px-4 py-3">{s.groomer_hint}</p>}
      <div className="flex flex-wrap items-center gap-3">
        {devMode && (
          <Button onClick={() => setEditing({ id: null, form: emptyProduct() })} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="add-product-btn">
            <Plus className="h-4 w-4 mr-2" /> {s.add_product}
          </Button>
        )}
        <div className="relative flex-1 min-w-52 max-w-sm">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={s.search_products} className={`${inputCls} pl-9`} data-testid="products-search" />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand/40" />
        </div>
        {q && <span className="text-xs font-bold text-brand-ink/50" data-testid="products-search-count">{filtered.length}/{products.length}</span>}
      </div>
      {products.length === 0 ? (
        <div className={`${lightCard} text-center text-brand-ink/50`} data-testid="shop-products-empty">{s.empty_products}</div>
      ) : filtered.length === 0 ? (
        <div className={`${lightCard} text-center text-brand-ink/50`} data-testid="shop-products-no-results">{s.search_no_results}</div>
      ) : (
        filtered.map((p) => (
          <div
            key={p.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onProdDrop(p.id)}
            className={lightCard}
            data-testid={`admin-product-${p.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-4 min-w-0">
                <span
                  draggable={!query.trim()}
                  onDragStart={() => { dragProd.current = p.id; }}
                  className="mt-1 grid place-items-center shrink-0 text-brand/30 hover:text-brand cursor-grab active:cursor-grabbing"
                  title={s.drag_hint}
                  data-testid={`product-drag-${p.id}`}
                >
                  <GripVertical className="h-5 w-5" />
                </span>
                <div className="h-16 w-16 rounded-xl bg-stone-alt/50 overflow-hidden shrink-0 grid place-items-center">
                  {p.images?.[0] ? <img src={`${API}/shop/image/${p.id}/0`} alt="" className="h-full w-full object-cover" /> : <Package className="h-6 w-6 text-brand/30" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-lg font-bold text-brand-ink">{p.name_gr}</h3>
                    {!p.active && <span className="rounded-full bg-stone-alt text-brand-ink/50 text-[10px] font-bold px-2 py-0.5 uppercase">inactive</span>}
                  </div>
                  <p className="text-xs text-brand-ink/50">{[p.brand, catMap[p.category_id], supMap[p.supplier_id]].filter(Boolean).join(" · ")}</p>
                  <p className="text-sm font-bold text-brand mt-0.5">€{Number(p.price).toFixed(2)}</p>
                </div>
              </div>
              {devMode && (
                <div className="flex items-center gap-2">
                  <Button onClick={() => setEditing({ id: p.id, form: { ...p, price: String(p.price), category_id: p.category_id || "", supplier_id: p.supplier_id || "", online_orderable: p.online_orderable !== false, sizes: p.sizes || [], colors: p.colors || [], custom_axis_name_gr: p.custom_axis_name_gr || "", custom_axis_name_en: p.custom_axis_name_en || "", custom_axis_options: p.custom_axis_options || [], variants: p.variants.map((v) => ({ ...v, lead_days: v.lead_days ?? "", price: v.price ?? "", sale_price: v.sale_price ?? "" })) } })} variant="ghost" size="icon" className="rounded-full text-brand hover:bg-brand-soft/50" data-testid={`edit-product-${p.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => del(p)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50" data-testid={`delete-product-${p.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* variants + availability (both roles) */}
            <div className="mt-3 space-y-2">
              {p.variants.map((v) => (
                <div key={v.id} className="flex flex-wrap items-center gap-2 rounded-xl bg-stone-alt/40 px-3 py-2" data-testid={`variant-row-${v.id}`}>
                  <span className="text-sm font-bold text-brand-ink min-w-20 inline-flex items-center gap-1.5">
                    {v.color && colorsLib.find((c) => c.id === v.color)?.image && <img src={`${API}/shop/color-image/${v.color}`} alt="" className="h-5 w-5 rounded-full object-cover border border-brand/10" />}
                    {v.label || [v.size, v.color ? (colorsLib.find((c) => c.id === v.color)?.name_gr || "") : "", v.custom].filter(Boolean).join(" / ") || "—"}
                  </span>
                  <span className="text-xs text-brand-ink/50">{s.stock}: <b className="text-brand-ink">{v.stock}</b></span>
                  <Select value={v.availability} onValueChange={(val) => setAvail(p, v.id, { availability: val })}>
                    <SelectTrigger className="w-[170px] h-8 rounded-full border-brand/20 text-xs" data-testid={`avail-select-${v.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in_stock">{shop.avail_in_stock}</SelectItem>
                      <SelectItem value="on_order">{shop.avail_on_order}</SelectItem>
                      <SelectItem value="unavailable">{shop.avail_unavailable}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number" min={0} defaultValue={v.stock}
                    onBlur={(e) => String(e.target.value) !== String(v.stock) && setAvail(p, v.id, { stock: e.target.value })}
                    className="w-20 h-8 rounded-lg border-brand/20 text-xs"
                    data-testid={`stock-input-${v.id}`}
                    title={s.stock}
                  />
                  {v.availability === "on_order" && (
                    <Input
                      type="number" min={1} max={90} defaultValue={v.lead_days ?? ""} placeholder="π.χ. 5"
                      onBlur={(e) => String(e.target.value) !== String(v.lead_days ?? "") && setAvail(p, v.id, { lead_days: e.target.value })}
                      className="w-24 h-8 rounded-lg border-brand/20 text-xs"
                      data-testid={`lead-input-${v.id}`}
                      title={s.eta}
                    />
                  )}
                  {devMode && (
                    <>
                      <Input
                        type="number" min="0" step="0.01" defaultValue={v.price ?? ""} placeholder={`€${Number(p.price).toFixed(2)}`}
                        onBlur={(e) => e.target.value !== String(v.price ?? "") && setPrice(p, v.id, { price: e.target.value })}
                        className="w-24 h-8 rounded-lg border-brand/20 text-xs"
                        data-testid={`price-input-${v.id}`}
                        title={s.price_inline}
                      />
                      <Input
                        type="number" min="0" step="0.01" defaultValue={v.sale_price ?? ""} placeholder="—"
                        onBlur={(e) => e.target.value !== String(v.sale_price ?? "") && setPrice(p, v.id, { sale_price: e.target.value })}
                        className="w-24 h-8 rounded-lg border-rose-200 text-xs"
                        data-testid={`sale-input-${v.id}`}
                        title={s.sale_inline}
                      />
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* images (dev) — unlimited, each assignable to a variant */}
            {devMode && (
              <div className="mt-3 flex items-start gap-3 flex-wrap">
                {(p.images || []).map((img, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-1.5">
                    <div className="relative h-14 w-14 rounded-lg overflow-hidden border border-brand/10 group">
                      <img src={`${API}/shop/image/${p.id}/${idx}`} alt="" className="h-full w-full object-cover" />
                      <button onClick={() => deleteImage(p, idx)} className="absolute inset-0 grid place-items-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white" data-testid={`delete-image-${p.id}-${idx}`}>
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {p.variants.some((v) => v.label) && (
                      <select
                        value={img.variant_id || ""}
                        onChange={(e) => assignImage(p, idx, e.target.value)}
                        className="max-w-[88px] rounded-md border border-brand/20 bg-white text-[10px] font-semibold text-brand-ink px-1 py-0.5"
                        data-testid={`image-variant-${p.id}-${idx}`}
                      >
                        <option value="">{s.img_all}</option>
                        {p.variants.filter((v) => v.label).map((v) => (
                          <option key={v.id} value={v.id}>{v.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
                <label className="h-14 w-14 rounded-lg border-2 border-dashed border-brand/20 grid place-items-center text-brand/50 hover:text-brand hover:border-brand/40 cursor-pointer transition-colors" data-testid={`upload-image-${p.id}`} title={s.images}>
                  <ImagePlus className="h-5 w-5" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(p, e.target.files?.[0])} />
                </label>
              </div>
            )}
          </div>
        ))
      )}

      {/* create/edit dialog (dev) */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="product-dialog">
          <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-cream">{editing?.id ? s.edit_product : s.add_product}</DialogTitle>
            </DialogHeader>
          </div>
          {editing && (
            <div className="p-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label className="text-brand-ink font-semibold">{s.name_gr}</Label>
                  <Input value={editing.form.name_gr} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name_gr: e.target.value } })} className={`${inputCls} mt-1`} data-testid="prod-name-gr" />
                </div>
                <div>
                  <Label className="text-brand-ink font-semibold">{s.name_en}</Label>
                  <Input value={editing.form.name_en} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name_en: e.target.value } })} className={`${inputCls} mt-1`} data-testid="prod-name-en" />
                </div>
                <div>
                  <Label className="text-brand-ink font-semibold">{s.price}</Label>
                  <Input type="number" step="0.01" min="0" value={editing.form.price} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, price: e.target.value } })} className={`${inputCls} mt-1`} data-testid="prod-price" />
                </div>
                <div>
                  <Label className="text-brand-ink font-semibold">{s.brand}</Label>
                  <Input value={editing.form.brand} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, brand: e.target.value } })} className={`${inputCls} mt-1`} data-testid="prod-brand" />
                </div>
                <div>
                  <Label className="text-brand-ink font-semibold">{s.category}</Label>
                  <Select value={editing.form.category_id || "none"} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, category_id: v === "none" ? "" : v } })}>
                    <SelectTrigger className={`${inputCls} mt-1`} data-testid="prod-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{s.none}</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_gr}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-brand-ink font-semibold">{s.supplier}</Label>
                  <Select value={editing.form.supplier_id || "none"} onValueChange={(v) => setEditing({ ...editing, form: { ...editing.form, supplier_id: v === "none" ? "" : v } })}>
                    <SelectTrigger className={`${inputCls} mt-1`} data-testid="prod-supplier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{s.none}</SelectItem>
                      {suppliers.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-brand-ink font-semibold">{s.description_gr}</Label>
                <div className="mt-1">
                  <RichTextEditor value={editing.form.description_gr} onChange={(html) => setEditing({ ...editing, form: { ...editing.form, description_gr: html } })} testid="prod-desc-gr" />
                </div>
              </div>
              <div>
                <Label className="text-brand-ink font-semibold">{s.description_en}</Label>
                <div className="mt-1">
                  <RichTextEditor value={editing.form.description_en} onChange={(html) => setEditing({ ...editing, form: { ...editing.form, description_en: html } })} testid="prod-desc-en" />
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-brand/10 bg-white p-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-brand-ink font-semibold">{s.sizes_label}</Label>
                    <div className="mt-1">
                      <ChipInput values={editing.form.sizes} onChange={(vs) => setEditing({ ...editing, form: { ...editing.form, sizes: vs } })} placeholder={s.sizes_ph} hint={s.sizes_hint} testid="prod-sizes" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{s.colors_label}</Label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {colorsLib.length === 0 && <p className="text-[11px] text-brand-ink/45">{s.colors_empty_hint}</p>}
                      {colorsLib.map((c) => {
                        const on = (editing.form.colors || []).includes(c.id);
                        return (
                          <button key={c.id} type="button" onClick={() => { const cs = on ? editing.form.colors.filter((x) => x !== c.id) : [...(editing.form.colors || []), c.id]; setEditing({ ...editing, form: { ...editing.form, colors: cs } }); }} className={`inline-flex items-center gap-1.5 rounded-full border pl-1 pr-2.5 py-1 text-xs font-bold transition-colors ${on ? "border-brand bg-brand-soft/70 text-brand-ink" : "border-brand/15 bg-stone-alt/30 text-brand-ink/60 hover:border-brand/40"}`} data-testid={`prod-color-${c.id}`}>
                            <span className="h-5 w-5 rounded-full overflow-hidden border border-brand/15 bg-white grid place-items-center shrink-0">
                              {c.image ? <img src={`${API}/shop/color-image/${c.id}`} alt="" className="h-full w-full object-cover" /> : <Package className="h-2.5 w-2.5 text-brand/30" />}
                            </span>
                            {c.name_gr}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label className="text-brand-ink font-semibold">{s.custom_axis_name_gr}</Label>
                    <Input value={editing.form.custom_axis_name_gr} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, custom_axis_name_gr: e.target.value } })} className={`${inputCls} mt-1`} data-testid="prod-custom-name-gr" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{s.custom_axis_name_en}</Label>
                    <Input value={editing.form.custom_axis_name_en} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, custom_axis_name_en: e.target.value } })} className={`${inputCls} mt-1`} data-testid="prod-custom-name-en" />
                  </div>
                  <div>
                    <Label className="text-brand-ink font-semibold">{s.custom_axis_options}</Label>
                    <div className="mt-1">
                      <ChipInput values={editing.form.custom_axis_options} onChange={(vs) => setEditing({ ...editing, form: { ...editing.form, custom_axis_options: vs } })} placeholder={s.custom_axis_ph} testid="prod-custom-opts" />
                    </div>
                  </div>
                </div>

                {(editing.form.sizes.length > 0 || editing.form.colors.length > 0) && (
                  <div>
                    <Button type="button" variant="outline" size="sm" className="rounded-full border-brand/30 text-brand font-bold" data-testid="combos-generate"
                      onClick={() => {
                        const f = editing.form;
                        const sizes = f.sizes.length ? f.sizes : [""];
                        const colors = f.colors.length ? f.colors : [""];
                        const customs = f.custom_axis_options.length ? f.custom_axis_options : [""];
                        const combos = [];
                        for (const sz of sizes) for (const co of colors) for (const cu of customs) {
                          const existing = f.variants.find((v) => (v.size || "") === sz && (v.color || "") === co && (v.custom || "") === cu && (sz || co || cu));
                          combos.push(existing || { label: "", size: sz, color: co, custom: cu, stock: 0, availability: "in_stock", lead_days: "", price: "", sale_price: "" });
                        }
                        const manual = f.variants.filter((v) => !(v.size || v.color || v.custom) && (v.label || "").trim());
                        setEditing({ ...editing, form: { ...f, variants: [...combos, ...manual] } });
                        toast.success(s.saved_toast);
                      }}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" /> {s.combos_generate}
                    </Button>
                    <p className="mt-1 text-[11px] text-brand-ink/45">{s.combos_replace_hint}</p>
                  </div>
                )}

                <div>
                  <Label className="text-brand-ink font-semibold">{s.variants}</Label>
                  <div className="mt-2 space-y-2">
                    {editing.form.variants.map((v, i) => {
                      const combo = v.size || v.color || v.custom;
                      const setV = (patch) => { const vs = [...editing.form.variants]; vs[i] = { ...v, ...patch }; setEditing({ ...editing, form: { ...editing.form, variants: vs } }); };
                      return (
                        <div key={i} className="flex flex-wrap items-center gap-2 rounded-xl bg-stone-alt/40 p-2.5" data-testid={`variant-edit-row-${i}`}>
                          {combo ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand/15 px-2.5 py-1 text-xs font-bold text-brand-ink min-w-24" data-testid={`variant-combo-label-${i}`}>
                              {v.color && colorsLib.find((c) => c.id === v.color)?.image && <img src={`${API}/shop/color-image/${v.color}`} alt="" className="h-4 w-4 rounded-full object-cover" />}
                              {[v.size, v.color ? (colorsLib.find((c) => c.id === v.color)?.name_gr || "") : "", v.custom].filter(Boolean).join(" / ") || "—"}
                            </span>
                          ) : (
                            <Input value={v.label} onChange={(e) => setV({ label: e.target.value })} placeholder={s.variant_label} className={`${inputCls} flex-1 min-w-36`} data-testid={`variant-label-${i}`} />
                          )}
                          <Input type="number" min="0" step="0.01" value={v.price} onChange={(e) => setV({ price: e.target.value })} placeholder={s.variant_price_ph} className={`${inputCls} w-32`} data-testid={`variant-price-${i}`} title={s.price} />
                          <Input type="number" min="0" step="0.01" value={v.sale_price} onChange={(e) => setV({ sale_price: e.target.value })} placeholder={s.variant_sale_ph} className={`${inputCls} w-36 border-rose-200`} data-testid={`variant-sale-${i}`} title={s.variant_sale_ph} />
                          <Input type="number" min="0" value={v.stock} onChange={(e) => setV({ stock: e.target.value })} className={`${inputCls} w-20`} data-testid={`variant-stock-${i}`} title={s.stock} />
                          <Select value={v.availability} onValueChange={(val) => setV({ availability: val })}>
                            <SelectTrigger className={`${inputCls} w-[150px]`} data-testid={`variant-avail-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="in_stock">{shop.avail_in_stock}</SelectItem>
                              <SelectItem value="on_order">{shop.avail_on_order}</SelectItem>
                              <SelectItem value="unavailable">{shop.avail_unavailable}</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input type="number" min="1" max="90" value={v.lead_days} onChange={(e) => setV({ lead_days: e.target.value })} className={`${inputCls} w-28`} placeholder={s.eta} data-testid={`variant-lead-${i}`} title={s.eta} />
                          {editing.form.variants.length > 1 && (
                            <button onClick={() => { const vs = editing.form.variants.filter((_, k) => k !== i); setEditing({ ...editing, form: { ...editing.form, variants: vs } }); }} className="text-rose-500 hover:bg-rose-50 rounded-full p-1.5" data-testid={`variant-remove-${i}`}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <Button onClick={() => setEditing({ ...editing, form: { ...editing.form, variants: [...editing.form.variants, { label: "", size: "", color: "", custom: "", stock: 0, availability: "in_stock", lead_days: "", price: "", sale_price: "" }] } })} variant="outline" size="sm" className="mt-2 rounded-full border-brand/30 text-brand font-bold" data-testid="variant-add">
                    <Plus className="h-4 w-4 mr-1" /> {s.add_variant}
                  </Button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                <input type="checkbox" checked={editing.form.active} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, active: e.target.checked } })} className="h-4 w-4 accent-brand" data-testid="prod-active" />
                {s.active}
              </label>

              <div className="rounded-xl bg-stone-alt/40 px-4 py-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                  <input type="checkbox" checked={editing.form.online_orderable} onChange={(e) => setEditing({ ...editing, form: { ...editing.form, online_orderable: e.target.checked } })} className="h-4 w-4 accent-brand" data-testid="prod-online-orderable" />
                  {s.online_orderable}
                </label>
                <p className="mt-1 text-xs text-brand-ink/50 pl-6">{s.online_orderable_hint}</p>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full border-brand/30 text-brand font-bold" data-testid="prod-cancel">{s.cancel}</Button>
                <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="prod-save">{s.save}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Categories (dev)
// ---------------------------------------------------------------------------
export const ShopCategoriesPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name_gr: "", name_en: "" });
  const [imgVer, setImgVer] = useState(0);
  const dragCat = useRef(null);

  const load = useCallback(() => {
    api.get("/shop/categories").then((r) => { setItems(r.data); setImgVer(Date.now()); }).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const onCatDrop = async (targetId) => {
    const from = dragCat.current;
    dragCat.current = null;
    if (!from || from === targetId) return;
    const ids = items.map((c) => c.id);
    const next = ids.filter((x) => x !== from);
    next.splice(next.indexOf(targetId), 0, from);
    setItems(next.map((id) => items.find((c) => c.id === id)));
    try {
      await api.put("/admin/shop/categories/reorder", { ids: next });
    } catch {
      toast.error("Error");
      load();
    }
  };

  const add = async () => {
    if (!form.name_gr.trim()) return;
    await api.post("/admin/shop/categories", { ...form, sort: (items?.length || 0) + 1 });
    setForm({ name_gr: "", name_en: "" });
    toast.success(s.saved_toast);
    load();
  };

  const uploadCatImage = async (c, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/shop/categories/${c.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(s.saved_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm(s.delete_confirm)) return;
    await api.delete(`/admin/shop/categories/${id}`);
    load();
  };

  if (items === null) return null;
  return (
    <div className="space-y-4" data-testid="shop-categories-panel">
      <div className={`${lightCard} flex flex-wrap gap-3 items-end`}>
        <div className="flex-1 min-w-40">
          <Label className="text-brand-ink font-semibold">{s.name_gr}</Label>
          <Input value={form.name_gr} onChange={(e) => setForm({ ...form, name_gr: e.target.value })} className={`${inputCls} mt-1`} data-testid="cat-name-gr" />
        </div>
        <div className="flex-1 min-w-40">
          <Label className="text-brand-ink font-semibold">{s.name_en}</Label>
          <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className={`${inputCls} mt-1`} data-testid="cat-name-en" />
        </div>
        <Button onClick={add} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="cat-add">
          <Plus className="h-4 w-4 mr-1" /> {s.add_category}
        </Button>
      </div>
      {items.length === 0 ? (
        <div className={`${lightCard} text-center text-brand-ink/50`} data-testid="categories-empty">{s.empty_categories}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {items.map((c) => (
            <div
              key={c.id}
              draggable
              onDragStart={() => { dragCat.current = c.id; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onCatDrop(c.id)}
              className={`${lightCard} flex items-center gap-3 cursor-grab active:cursor-grabbing`}
              data-testid={`category-${c.id}`}
            >
              <GripVertical className="h-4 w-4 text-brand/30 shrink-0" />
              <label className="relative h-14 w-14 rounded-xl overflow-hidden border border-brand/15 bg-stone-alt/50 grid place-items-center cursor-pointer shrink-0 group" title={s.cat_photo} data-testid={`cat-photo-${c.id}`}>
                {c.image ? <img src={`${API}/${c.image}?v=${imgVer}`} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-brand/40" />}
                <span className="absolute inset-0 grid place-items-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ImagePlus className="h-4 w-4" /></span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCatImage(c, e.target.files?.[0])} />
              </label>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-brand-ink truncate">{c.name_gr}</p>
                {c.name_en && <p className="text-xs text-brand-ink/50">{c.name_en}</p>}
              </div>
              <Button onClick={() => del(c.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 shrink-0" data-testid={`cat-delete-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Colour library (dev) — reusable colours with their own small photo
// ---------------------------------------------------------------------------
export const ShopColorsPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name_gr: "", name_en: "" });
  const [imgVer, setImgVer] = useState(0);

  const load = useCallback(() => {
    api.get("/shop/colors").then((r) => { setItems(r.data); setImgVer(Date.now()); }).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!form.name_gr.trim()) return;
    await api.post("/admin/shop/colors", form);
    setForm({ name_gr: "", name_en: "" });
    toast.success(s.saved_toast);
    load();
  };

  const rename = async (c, patch) => {
    await api.put(`/admin/shop/colors/${c.id}`, { name_gr: patch.name_gr ?? c.name_gr, name_en: patch.name_en ?? (c.name_en || "") });
    load();
  };

  const upload = async (c, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/shop/colors/${c.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(s.saved_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const del = async (c) => {
    if (!window.confirm(s.delete_confirm)) return;
    await api.delete(`/admin/shop/colors/${c.id}`);
    toast.success(s.deleted_toast);
    load();
  };

  if (items === null) return null;
  return (
    <div className="space-y-4" data-testid="shop-colors-panel">
      <p className="text-sm text-brand-ink/60">{s.colors_hint}</p>
      <div className={`${lightCard} flex flex-wrap gap-3 items-end`}>
        <div className="flex-1 min-w-40">
          <Label className="text-brand-ink font-semibold">{s.color_name_gr}</Label>
          <Input value={form.name_gr} onChange={(e) => setForm({ ...form, name_gr: e.target.value })} className={`${inputCls} mt-1`} data-testid="color-name-gr" />
        </div>
        <div className="flex-1 min-w-40">
          <Label className="text-brand-ink font-semibold">{s.color_name_en}</Label>
          <Input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className={`${inputCls} mt-1`} data-testid="color-name-en" />
        </div>
        <Button onClick={add} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="color-add">
          <Plus className="h-4 w-4 mr-1" /> {s.add_color}
        </Button>
      </div>
      {items.length === 0 ? (
        <div className={`${lightCard} text-center text-brand-ink/50`} data-testid="colors-empty">{s.empty_colors}</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} className={`${lightCard} flex items-center gap-3`} data-testid={`color-${c.id}`}>
              <label className="relative h-12 w-12 rounded-full overflow-hidden border border-brand/15 bg-stone-alt/50 grid place-items-center cursor-pointer shrink-0 group" title={s.color_photo} data-testid={`color-photo-${c.id}`}>
                {c.image ? <img src={`${API}/shop/color-image/${c.id}?v=${imgVer}`} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-4 w-4 text-brand/40" />}
                <span className="absolute inset-0 grid place-items-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ImagePlus className="h-4 w-4" /></span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(c, e.target.files?.[0])} />
              </label>
              <div className="min-w-0 flex-1 space-y-1">
                <Input defaultValue={c.name_gr} onBlur={(e) => e.target.value !== c.name_gr && rename(c, { name_gr: e.target.value })} className={`${inputCls} h-8 text-sm font-bold`} data-testid={`color-rename-${c.id}`} />
                <Input defaultValue={c.name_en || ""} placeholder="EN" onBlur={(e) => e.target.value !== (c.name_en || "") && rename(c, { name_en: e.target.value })} className={`${inputCls} h-8 text-xs`} data-testid={`color-rename-en-${c.id}`} />
              </div>
              <Button onClick={() => del(c)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 shrink-0" data-testid={`color-delete-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Brand cover photos (dev) — brands are free-text on products; only the photo is stored
// ---------------------------------------------------------------------------
export const ShopBrandPhotosPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [brands, setBrands] = useState(null);
  const [imgVer, setImgVer] = useState(0);
  const dragBrand = useRef(null);

  const load = useCallback(() => {
    api.get("/shop/brands").then((r) => { setBrands(r.data); setImgVer(Date.now()); }).catch(() => setBrands([]));
  }, []);
  useEffect(load, [load]);

  const onBrandDrop = async (targetName) => {
    const from = dragBrand.current;
    dragBrand.current = null;
    if (!from || from === targetName) return;
    const names = brands.map((b) => b.name);
    const next = names.filter((x) => x !== from);
    next.splice(next.indexOf(targetName), 0, from);
    setBrands(next.map((n) => brands.find((b) => b.name === n)));
    try {
      await api.put("/admin/shop/brands/reorder", { names: next });
    } catch {
      toast.error("Error");
      load();
    }
  };

  const upload = async (b, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("name", b.name);
    fd.append("file", file);
    try {
      await api.post("/admin/shop/brands/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(s.saved_toast);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (brands === null) return null;
  if (brands.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="brand-photos-panel">
      <p className="font-heading text-xl font-bold text-brand-ink">{s.brands_photos_title}</p>
      <p className="text-xs text-brand-ink/50">{s.brands_photos_hint}</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {brands.map((b) => (
          <div
            key={b.name}
            draggable
            onDragStart={() => { dragBrand.current = b.name; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onBrandDrop(b.name)}
            className={`${lightCard} flex items-center gap-3 cursor-grab active:cursor-grabbing group`}
            data-testid={`brand-photo-${b.name}`}
          >
            <GripVertical className="h-4 w-4 text-brand/30 shrink-0" />
            <label className="relative h-14 w-14 rounded-xl overflow-hidden border border-brand/15 bg-stone-alt/50 grid place-items-center shrink-0 cursor-pointer" title={s.brand_photo || "Φωτογραφία"} data-testid={`brand-photo-upload-${b.name}`}>
              {b.image ? <img src={`${API}/${b.image}?v=${imgVer}`} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-brand/40" />}
              <span className="absolute inset-0 grid place-items-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity"><ImagePlus className="h-4 w-4" /></span>
              <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(b, e.target.files?.[0])} />
            </label>
            <span className="min-w-0 flex-1">
              <span className="block font-bold text-brand-ink truncate">{b.name}</span>
              <span className="block text-xs text-brand-ink/50">{b.count} {b.count === 1 ? "προϊόν" : "προϊόντα"}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Suppliers CRUD (dev)
// ---------------------------------------------------------------------------
// Suppliers CRUD (dev)
// ---------------------------------------------------------------------------
export const ShopSuppliersPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", contact: "", email: "", phone: "", notes: "" });

  const load = useCallback(() => {
    api.get("/admin/shop/suppliers").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);

  const add = async () => {
    if (!form.name.trim()) return;
    await api.post("/admin/shop/suppliers", form);
    setForm({ name: "", contact: "", email: "", phone: "", notes: "" });
    toast.success(s.saved_toast);
    load();
  };

  const del = async (id) => {
    if (!window.confirm(s.delete_confirm)) return;
    await api.delete(`/admin/shop/suppliers/${id}`);
    load();
  };

  if (items === null) return null;
  return (
    <div className="space-y-4" data-testid="shop-suppliers-panel">
      <div className={`${lightCard} grid gap-3 sm:grid-cols-2 lg:grid-cols-5 items-end`}>
        <div>
          <Label className="text-brand-ink font-semibold">{s.name_gr}</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inputCls} mt-1`} data-testid="sup-name" />
        </div>
        <div>
          <Label className="text-brand-ink font-semibold">{s.contact}</Label>
          <Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className={`${inputCls} mt-1`} data-testid="sup-contact" />
        </div>
        <div>
          <Label className="text-brand-ink font-semibold">Email</Label>
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} mt-1`} data-testid="sup-email" />
        </div>
        <div>
          <Label className="text-brand-ink font-semibold">{t.admin.email ? "" : ""}Τηλέφωνο</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inputCls} mt-1`} data-testid="sup-phone" />
        </div>
        <Button onClick={add} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="sup-add">
          <Plus className="h-4 w-4 mr-1" /> {s.add_supplier}
        </Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {items.map((x) => (
          <div key={x.id} className={`${lightCard} flex items-start justify-between`} data-testid={`supplier-${x.id}`}>
            <div>
              <p className="font-bold text-brand-ink">{x.name}</p>
              <p className="text-xs text-brand-ink/50">{[x.contact, x.email, x.phone].filter(Boolean).join(" · ") || "—"}</p>
            </div>
            <Button onClick={() => del(x.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50" data-testid={`sup-delete-${x.id}`}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stats (dev)
// ---------------------------------------------------------------------------
export const ShopStatsPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/shop/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, []);
  if (!stats) return null;
  return (
    <div className="space-y-5" data-testid="shop-stats-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.stats_total}</p><p className="font-heading text-3xl font-bold text-brand-ink mt-1" data-testid="stats-total">{stats.total_orders ?? 0}</p></div>
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.new_orders}</p><p className="font-heading text-3xl font-bold text-sky-700 mt-1" data-testid="stats-new">{stats.by_status?.new ?? 0}</p></div>
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.stats_revenue}</p><p className="font-heading text-3xl font-bold text-bamboo-deep mt-1" data-testid="stats-revenue">€{(stats.revenue_completed ?? 0).toFixed(2)}</p></div>
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.stats_ready_value}</p><p className="font-heading text-3xl font-bold text-brand mt-1" data-testid="stats-ready-value">€{(stats.ready_value ?? 0).toFixed(2)}</p></div>
      </div>
      <div className={lightCard}>
        <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 mb-3 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-brand" /> {s.stats_top}</p>
        {(stats.top_products || []).length === 0 ? (
          <p className="text-sm text-brand-ink/40 italic">—</p>
        ) : (
          <div className="space-y-2">
            {stats.top_products.map((p) => (
              <div key={p.name} className="flex items-center justify-between text-sm">
                <span className="text-brand-ink/80 font-semibold">{p.name}</span>
                <span className="font-bold text-brand">{s.qty_short}{p.qty}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Eshop settings (dev): enable/disable, hide from menu, preview password
// ---------------------------------------------------------------------------
export const ShopSettingsPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/shop/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/admin/shop/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const save = async (patch) => {
    // Toggle saves never touch the password — avoids accidental overwrites
    const next = { ...settings, ...patch };
    setSettings(next);
    setSaving(true);
    try {
      await api.put("/admin/shop/settings", {
        enabled: next.enabled,
        hidden: next.hidden,
        home_section: next.home_section,
        colors_badge: next.colors_badge,
        featured_ids: next.featured_ids,
      });
      toast.success(s.settings_saved);
      refreshShopStatus();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleFeatured = (pid) => {
    const cur = settings.featured_ids || [];
    const next = cur.includes(pid) ? cur.filter((x) => x !== pid) : cur.length >= 8 ? cur : [...cur, pid];
    save({ featured_ids: next });
  };

  const savePassword = async () => {
    setSaving(true);
    try {
      await api.put("/admin/shop/settings", { enabled: settings.enabled, hidden: settings.hidden, home_section: settings.home_section, colors_badge: !!settings.colors_badge, access_password: pw.trim() });
      setPw("");
      toast.success(s.settings_saved);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;
  const Toggle = ({ checked, onChange, label, testid }) => (
    <button onClick={() => onChange(!checked)} className="flex items-center justify-between w-full rounded-2xl bg-stone-alt/40 px-4 py-3.5 hover:bg-stone-alt/70 transition-colors" data-testid={testid}>
      <span className="text-sm font-bold text-brand-ink text-left">{label}</span>
      <span className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-bamboo" : "bg-brand-ink/20"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );

  const featuredSet = new Set(settings.featured_ids || []);

  return (
    <div className="space-y-4" data-testid="shop-settings-panel">
      <div className={`${lightCard} space-y-3`}>
        <p className="font-heading text-xl font-bold text-brand-ink flex items-center gap-2"><Settings2 className="h-5 w-5 text-brand" /> {s.settings_title}</p>
        <Toggle checked={settings.enabled} onChange={(v) => save({ enabled: v })} label={s.eshop_enabled} testid="eshop-enabled-toggle" />
        <Toggle checked={settings.hidden} onChange={(v) => save({ hidden: v })} label={s.eshop_hidden} testid="eshop-hidden-toggle" />
        <Toggle checked={settings.home_section !== false} onChange={(v) => save({ home_section: v })} label={s.home_section} testid="eshop-home-toggle" />
        <Toggle checked={!!settings.colors_badge} onChange={(v) => save({ colors_badge: v })} label={s.colors_badge} testid="eshop-colors-badge-toggle" />
      </div>

      {settings.enabled && settings.home_section !== false && (
        <div className={lightCard} data-testid="featured-picker">
          <p className="font-heading text-xl font-bold text-brand-ink">{s.featured_title}</p>
          <p className="text-xs text-brand-ink/50 mt-1">{s.featured_hint}</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {products.filter((p) => p.active !== false).map((p) => {
              const idx = (settings.featured_ids || []).indexOf(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => toggleFeatured(p.id)}
                  className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors no-a11y-underline ${
                    idx >= 0 ? "border-brand bg-brand-soft/60" : "border-brand/10 bg-stone-alt/30 hover:bg-stone-alt/60"
                  }`}
                  data-testid={`featured-toggle-${p.id}`}
                >
                  <div className="h-10 w-10 rounded-lg bg-white overflow-hidden shrink-0 grid place-items-center border border-brand/10">
                    {p.images?.[0] ? <img src={`${API}/shop/image/${p.id}/0`} alt="" className="h-full w-full object-cover" /> : <Package className="h-4 w-4 text-brand/30" />}
                  </div>
                  <span className="min-w-0 flex-1 text-sm font-bold text-brand-ink truncate">{p.name_gr}</span>
                  {idx >= 0 && <span className="grid place-items-center h-5 w-5 rounded-full bg-brand text-brand-cream text-[10px] font-bold shrink-0">{idx + 1}</span>}
                </button>
              );
            })}
          </div>
          {(settings.featured_ids || []).length === 0 && <p className="mt-3 text-xs text-brand-ink/40 italic">{s.featured_none}</p>}
        </div>
      )}

      <div className={lightCard}>
        <Label className="text-brand-ink font-semibold">{s.access_password}</Label>
        <p className="text-xs text-brand-ink/50 mt-0.5">{settings.has_password ? `✓ ${s.access_password_set}` : "—"}</p>
        <div className="mt-2 flex gap-2 max-w-sm">
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={s.access_password_ph} className={inputCls} data-testid="eshop-password-input" />
          <Button onClick={savePassword} disabled={saving || pw.trim().length < 4} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold" data-testid="eshop-password-save">
            {s.save}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Dev overview (mini)
// ---------------------------------------------------------------------------
export const ShopOverviewPanel = () => {
  const { t } = useLang();
  const s = t.ashop;
  const [orders, setOrders] = useState(null);
  const [suppliers, setSuppliers] = useState(null);
  const [products, setProducts] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/shop/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/admin/shop/supplier-orders").then((r) => setSuppliers(r.data)).catch(() => setSuppliers([]));
    api.get("/admin/shop/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);
  useEffect(load, [load]);
  if (orders === null || suppliers === null || products === null) return null;
  const newCount = orders.filter((o) => o.status === "new").length;
  const readyCount = orders.filter((o) => o.status === "ready").length;
  const pendingSupplier = suppliers.reduce((sum, g) => sum + g.items.length, 0);
  return (
    <div className="space-y-5" data-testid="shop-overview-panel">
      <div className="flex justify-end">
        <Button onClick={load} variant="ghost" size="icon" className="rounded-full text-brand hover:bg-brand-soft/50" data-testid="shop-overview-refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.new_orders}</p><p className="font-heading text-3xl font-bold text-sky-700 mt-1" data-testid="ov-shop-new">{newCount}</p></div>
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.ready_orders}</p><p className="font-heading text-3xl font-bold text-bamboo-deep mt-1" data-testid="ov-shop-ready">{readyCount}</p></div>
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.supplier_pending}</p><p className="font-heading text-3xl font-bold text-amber-700 mt-1" data-testid="ov-shop-supplier">{pendingSupplier}</p></div>
        <div className={lightCard}><p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/50">{s.total_products}</p><p className="font-heading text-3xl font-bold text-brand-ink mt-1" data-testid="ov-shop-products">{products.length}</p></div>
      </div>
      {newCount > 0 && (
        <div className={`${lightCard} border-sky-200 bg-sky-50/60`}>
          <p className="text-sm font-bold text-sky-800 flex items-center gap-2"><ShoppingBag className="h-4 w-4" /> {s.new_orders}: {newCount}</p>
        </div>
      )}
    </div>
  );
};
